import type { Dayjs } from 'dayjs';
import { storeIntlLocale } from '@/settings';

/**
 * Dates and numbers on the European stores, rendered by Intl in the store's
 * locale (fr-FR, de-DE, it-IT, es-ES, …) instead of hand-built strings: no
 * English day or month names, no ordinals. Every helper takes the locale,
 * `storeIntlLocale` by default, and returns null where it's null so non-EU
 * stores keep the format they had.
 */

const DATE_STYLES = {
  /** "jeudi 15 octobre", "Donnerstag, 15. Oktober" */
  full: { weekday: 'long', day: 'numeric', month: 'long' },
  /** "15 octobre", "15. Oktober" */
  dayMonth: { day: 'numeric', month: 'long' },
  /** "15 oct.", "15. Okt." */
  dayMonthShort: { day: 'numeric', month: 'short' },
  /** "15/10/26", "15.10.26" */
  short: { day: '2-digit', month: '2-digit', year: '2-digit' },
  /** "14 oct. 2026", "14. Okt. 2026" */
  order: { day: 'numeric', month: 'short', year: 'numeric' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

export type StoreDateStyleT = keyof typeof DATE_STYLES;

// The calendar day the Dayjs shows (after any `.tz()`), as a local Date, so
// Intl prints that day whatever timezone the server or browser runs in.
const toCalendarDate = (date: Dayjs): Date =>
  new Date(date.year(), date.month(), date.date());

export const formatStoreDate = (
  date: Dayjs,
  style: StoreDateStyleT,
  locale: string | null = storeIntlLocale,
): string | null =>
  locale && date.isValid()
    ? new Intl.DateTimeFormat(locale, DATE_STYLES[style]).format(
        toCalendarDate(date),
      )
    : null;

/** A range the way the locale writes it: "6.–7. Oktober", "6 oct. – 2 nov." */
export const formatStoreDateRange = (
  min: Dayjs,
  max: Dayjs,
  style: StoreDateStyleT,
  locale: string | null = storeIntlLocale,
): string | null => {
  if (!locale || !min.isValid() || !max.isValid()) {
    return null;
  }

  // `formatRange` (ES2021) isn't in this TS lib's types yet.
  const format = new Intl.DateTimeFormat(
    locale,
    DATE_STYLES[style],
  ) as Intl.DateTimeFormat & { formatRange: (from: Date, to: Date) => string };

  return min.isSame(max, 'day')
    ? format.format(toCalendarDate(max))
    : format.formatRange(toCalendarDate(min), toCalendarDate(max));
};

/**
 * A count or amount without a currency: "95 353" (fr), "95.353" (de, it, es).
 * Grouped from four digits too, where CLDR would leave Spanish and Italian
 * ungrouped ("2986"). Elsewhere, `toLocaleString()` as before.
 */
export const formatStoreNumber = (
  value: number,
  locale: string | null = storeIntlLocale,
): string =>
  locale
    ? new Intl.NumberFormat(locale, {
        // `'always'` isn't in this TS lib's types yet.
        useGrouping: 'always' as unknown as boolean,
      }).format(value)
    : value.toLocaleString();

/**
 * Reads back a number typed or shown by `formatStoreNumber`. On the EU stores
 * the locale's thousands separator (and any space) is dropped and its decimal
 * separator read as one, so "1.299" is 1299 and "12,5" is 12.5 in de-DE, not
 * 125; elsewhere only commas are dropped, as before. NaN when it isn't a
 * number.
 */
export const parseStoreNumber = (
  value: string,
  locale: string | null = storeIntlLocale,
): number => {
  if (!locale) {
    return Number(value.replace(/,/g, ''));
  }

  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
  const group = parts.find((part) => part.type === 'group')?.value ?? '';
  const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.';

  return Number(
    value.replace(/\s/g, '').split(group).join('').split(decimal).join('.'),
  );
};

/**
 * A figure with decimals, like a star rating: "4,7" on the EU stores. The
 * others keep the plain "4.7" they had (not `toLocaleString()`, which would
 * follow the browser's language).
 */
export const formatStoreDecimal = (
  value: number,
  locale: string | null = storeIntlLocale,
): string =>
  locale ? new Intl.NumberFormat(locale).format(value) : String(value);
